import React, { useState, useRef, useEffect } from 'react';
import { TextField, Stack, Button, Typography, Box, Chip, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { playErrorBeep } from '../utils/audio';

interface MultiRfidSearchComponentProps {
  onSearch: (rfids: string[]) => void;
  isSearching?: boolean;
}

const MultiRfidSearchComponent: React.FC<MultiRfidSearchComponentProps> = ({
  onSearch,
  isSearching = false,
}) => {
  const [rfidInput, setRfidInput] = useState('');
  const [rfidChips, setRfidChips] = useState<string[]>([]);
  const rfidInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the RFID input field on component mount
  useEffect(() => {
    rfidInputRef.current?.focus();
  }, []);

  const handleRfidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.length < 24) {
      setRfidInput(value);
      return;
    }

    const newRfids: string[] = [];
    const existingRfids = new Set(rfidChips);
    let currentString = '';
    let foundDuplicate = false;

    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      if (/[a-zA-Z0-9]/.test(char)) {
        currentString += char;

        if (currentString.length === 24) {
          if (!existingRfids.has(currentString) && !newRfids.includes(currentString)) {
            newRfids.push(currentString);
            existingRfids.add(currentString);
          } else {
            foundDuplicate = true;
          }
          currentString = '';
        }
      } else {
        currentString = '';
      }
    }

    // Play error beep if any duplicates were found during scanning
    if (foundDuplicate) {
      playErrorBeep();
    }

    if (newRfids.length > 0) {
      setRfidChips((prev) => [...prev, ...newRfids]);
    }

    setRfidInput(currentString);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (rfidChips.length > 0) {
        handleSearch();
      }
    }
  };

  const handleRemoveRfidChip = (rfidToRemove: string) => {
    setRfidChips((prev) => prev.filter((rfid) => rfid !== rfidToRemove));
  };

  const handleSearch = () => {
    if (rfidChips.length > 0) {
      onSearch(rfidChips);
    }
  };

  const handleClearAll = () => {
    setRfidChips([]);
    setRfidInput('');
    rfidInputRef.current?.focus();
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Scan or paste RFID codes. Completed scans appear as chips automatically. Use Search to find
        matching corals.
      </Typography>

      {/* RFID Input Field */}
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
        <TextField
          inputRef={rfidInputRef}
          value={rfidInput}
          onChange={handleRfidChange}
          onKeyDown={handleKeyDown}
          label="Scan or enter RFID"
          placeholder="e.g., 000000000000046536326313"
          variant="outlined"
          size="small"
          fullWidth
          disabled={isSearching}
          helperText={
            rfidChips.length > 0
              ? `${rfidChips.length} RFID${rfidChips.length > 1 ? 's' : ''} ready - Press Enter to search`
              : 'RFIDs will appear as chips below as you scan them'
          }
        />
      </Stack>

      {/* Selected RFIDs as Chips */}
      {rfidChips.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Selected RFIDs ({rfidChips.length}):
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {rfidChips.map((rfid) => (
              <Chip
                key={rfid}
                label={rfid}
                onDelete={() => handleRemoveRfidChip(rfid)}
                variant="outlined"
                size="medium"
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Search Controls */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={rfidChips.length === 0 || isSearching}
          startIcon={isSearching ? <CircularProgress size={16} /> : <SearchIcon />}
        >
          {isSearching ? 'Searching...' : 'Search'}
        </Button>
        <Button
          variant="outlined"
          onClick={handleClearAll}
          disabled={(rfidChips.length === 0 && !rfidInput) || isSearching}
          startIcon={<ClearIcon />}
        >
          Clear All
        </Button>
        {rfidChips.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {rfidChips.length} RFID{rfidChips.length !== 1 ? 's' : ''} ready to search
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default MultiRfidSearchComponent;
